import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CHANGE_SCALE,
  CONTACT_SCALE,
  scaleGradientCss,
  thicknessScale,
  type HeatmapScale,
} from '@/lib/geometry/heatmap';
import type { HeatmapMode, HeatmapStat, HeatmapStatus } from '../engine/types';
import { FloatingPanel } from './FloatingPanel';

/**
 * The key to whichever map is on, and the map in numbers.
 *
 * The words sit under the colours as well as the millimetres: "−0.20 mm" means
 * nothing at a glance, "through" does, and the dentist is reading this while
 * turning the model. Under that, one line per tooth says what the colours
 * amount to - the finding a dentist can write down and hold the lab to.
 */
type MapMode = Exclude<HeatmapMode, 'off'>;

interface HeatmapLegendProps {
  mode: MapMode;
  status: HeatmapStatus;
  stats: HeatmapStat[];
  minThickness: number;
  /** The version being compared against, in changes mode. */
  previousVersion: number | null;
  onMode: (mode: HeatmapMode) => void;
}

const ANALYSIS_MODES: { value: MapMode; label: string }[] = [
  { value: 'occlusal', label: 'Bite' },
  { value: 'proximal', label: 'Neighbours' },
  { value: 'thickness', label: 'Wall thickness' },
];

function mm(value: number): string {
  return `${Math.abs(value).toFixed(2)} mm`;
}

/** One tooth's numbers, in the words of the map they came from. */
function describe(mode: MapMode, stat: HeatmapStat, minimum: number): string {
  if (Number.isNaN(stat.min)) return 'Nothing near enough to measure';
  if (mode === 'thickness') {
    const thin =
      stat.share > 0
        ? `${String(Math.max(1, Math.round(stat.share * 100)))}% of the wall under ${mm(minimum)}`
        : `nowhere under ${mm(minimum)}`;
    return `Thinnest ${mm(stat.min)} · ${thin}`;
  }
  if (mode === 'changes') {
    if (stat.regions === 0) return 'No change over 0.05 mm';
    const parts: string[] = [];
    if (stat.min < -0.02) parts.push(`up to ${mm(stat.min)} removed`);
    if (stat.max > 0.02) parts.push(`up to ${mm(stat.max)} added`);
    return `${parts.join(', ')} · ${String(stat.regions)} area${stat.regions === 1 ? '' : 's'}`;
  }
  const areas = `${String(stat.regions)} contact area${stat.regions === 1 ? '' : 's'}`;
  if (stat.min < 0) return `${areas} · deepest ${mm(stat.min)} through`;
  if (stat.min <= 0.05) return `${areas} · touching`;
  return `No contact · closest ${mm(stat.min)}`;
}

function isConcern(mode: MapMode, stat: HeatmapStat, minimum: number): boolean {
  if (Number.isNaN(stat.min)) return false;
  if (mode === 'thickness') return stat.min < minimum;
  if (mode === 'changes') return false;
  return stat.min < -0.05;
}

export function HeatmapLegend({
  mode,
  status,
  stats,
  minThickness,
  previousVersion,
  onMode,
}: HeatmapLegendProps) {
  const scale: HeatmapScale =
    mode === 'thickness'
      ? thicknessScale(minThickness)
      : mode === 'changes'
        ? CHANGE_SCALE
        : CONTACT_SCALE;
  const title = mode === 'changes' ? `Changes since v${String(previousVersion ?? '')}` : 'Heatmap';

  return (
    <FloatingPanel className="w-[min(28rem,calc(100vw-1.5rem))] px-3 py-2.5">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100">
          {title}
        </span>
        {status === 'computing' && <Loader2 className="size-3.5 animate-spin text-neutral-400" />}
        {mode !== 'changes' && (
          <div
            role="radiogroup"
            aria-label="Map"
            className="ml-auto flex gap-0.5 rounded-md bg-neutral-900/5 p-0.5 dark:bg-white/10"
          >
            {ANALYSIS_MODES.map((item) => (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={mode === item.value}
                onClick={() => {
                  onMode(item.value);
                }}
                className={cn(
                  'h-6 rounded px-2 text-[11.5px] font-medium whitespace-nowrap',
                  mode === item.value
                    ? 'bg-white shadow-sm dark:bg-neutral-800'
                    : 'text-neutral-600 dark:text-neutral-300',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          aria-label="Turn the map off"
          onClick={() => {
            onMode('off');
          }}
          className={cn(
            'rounded-md p-0.5 text-neutral-500 hover:bg-neutral-900/5 hover:text-neutral-900 dark:hover:bg-white/10 dark:hover:text-white',
            mode === 'changes' && 'ml-auto',
          )}
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="h-2.5 rounded-full" style={{ backgroundImage: scaleGradientCss(scale) }} />
      <div className="mt-1 grid grid-cols-3 font-mono text-[10.5px] text-neutral-500 tabular-nums">
        <span>{mode === 'thickness' ? '0' : `−${mm(scale.min)}`}</span>
        <span className="text-center">{mode === 'thickness' ? '' : '0'}</span>
        <span className="text-right">{`${mode === 'thickness' ? '' : '+'}${mm(scale.max)}`}</span>
      </div>
      <div className="grid grid-cols-3 text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
        <span>{scale.words[0]}</span>
        <span className="text-center">{scale.words[1]}</span>
        <span className="text-right">{scale.words[2]}</span>
      </div>

      {status === 'ready' && stats.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-neutral-900/5 pt-2 dark:border-white/10">
          {stats.map((stat) => (
            <li key={stat.tooth} className="flex items-baseline gap-2 text-[12px]">
              <span className="w-6 shrink-0 font-mono font-semibold text-neutral-800 dark:text-neutral-100">
                {stat.tooth}
              </span>
              <span
                className={cn(
                  'text-neutral-600 dark:text-neutral-300',
                  isConcern(mode, stat, minThickness) && 'font-medium text-danger-500',
                )}
              >
                {describe(mode, stat, minThickness)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {status === 'unavailable' && (
        <p className="mt-1.5 text-[11.5px] text-warning-500">
          {mode === 'changes'
            ? 'The previous version is not available for this case.'
            : 'Nothing to measure against in this case.'}
        </p>
      )}
    </FloatingPanel>
  );
}
